import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { 
  FileText, 
  Download, 
  RotateCcw, 
  DollarSign,
  BarChart3,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Building,
  Calendar
} from "lucide-react";
import axios from "axios";

const ScheduleHSummary = ({ calculationId, contracts, allocationResults }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [scheduleHData, setScheduleHData] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  const API = (process.env.REACT_APP_BACKEND_URL || "");

  useEffect(() => {
    if (calculationId) {
      loadScheduleH();
    }
  }, [calculationId]);

  const loadScheduleH = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/api/schedule-h/${calculationId}`);
      setScheduleHData(response.data);
      if (response.data.generated_timestamp) {
        setLastGenerated(new Date(response.data.generated_timestamp).getTime());
      }
      
    } catch (e) {
      if (e.response?.status !== 404) {
        setError(e.response?.data?.detail || "Error loading Schedule H");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateScheduleH = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`${API}/api/generate-schedule-h/${calculationId}`);
      
      setSuccess("Schedule H generated successfully!");
      
      // Reload data after successful generation
      setTimeout(() => {
        loadScheduleH();
        setSuccess(null);
      }, 2000);
      
    } catch (e) {
      setError(e.response?.data?.detail || "Error generating Schedule H");
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContractTypeLabel = (type) => {
    const types = {
      cpff: "CPFF",
      ffp: "FFP", 
      t_m: "T&M"
    };
    return types[type] || type?.toUpperCase();
  };

  const getContractStatusColor = (status) => {
    const colors = {
      active: "bg-green-50 text-green-700 border-green-200",
      completed: "bg-blue-50 text-blue-700 border-blue-200",
      terminated: "bg-red-50 text-red-700 border-red-200"
    };
    return colors[status] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  const calculateTotals = (data) => {
    if (!data?.contract_summaries) return { directCosts: 0, indirectCosts: 0, totalCosts: 0 };
    
    const totals = data.contract_summaries.reduce((acc, contract) => ({
      directCosts: acc.directCosts + (contract.total_direct_costs || 0),
      indirectCosts: acc.indirectCosts + (contract.total_indirect_costs || 0),
      totalCosts: acc.totalCosts + (contract.total_direct_costs || 0) + (contract.total_indirect_costs || 0)
    }), { directCosts: 0, indirectCosts: 0, totalCosts: 0 });
    
    return totals;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-sm text-slate-600">Loading Schedule H...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              <span>Schedule H - Summary by Contract</span>
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadScheduleH}
                disabled={loading}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleGenerateScheduleH}
                disabled={generating || !calculationId || contracts.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Download className="h-4 w-4 mr-2" />
                {generating ? "Generating..." : "Generate Schedule H"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Schedule H Status */}
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <FileText className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-900">Schedule H Status</span>
              </div>
              <div className="text-lg font-bold text-indigo-600">
                {scheduleHData ? "Generated" : "Not Generated"}
              </div>
              {scheduleHData && (
                <div className="text-xs text-indigo-700 mt-1">
                  {formatDate(scheduleHData.generated_timestamp)}
                </div>
              )}
            </div>
            
            {/* Contracts Summary */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Building className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Contracts</span>
              </div>
              <div className="text-lg font-bold text-blue-600">
                {scheduleHData?.contract_summaries?.length || contracts.length || 0}
              </div>
              {scheduleHData && (
                <div className="text-xs text-blue-700 mt-1">
                  Active contracts included
                </div>
              )}
            </div>

            {/* Total Contract Value */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-900">Total Contract Value</span>
              </div>
              <div className="text-lg font-bold text-green-600">
                {scheduleHData ? formatCurrency(calculateTotals(scheduleHData).totalCosts) : '$0.00'}
              </div>
              {scheduleHData && (
                <div className="text-xs text-green-700 mt-1">
                  Direct + Indirect costs
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedule H Data Display */}
      {scheduleHData ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                <span>Schedule H - Contract Cost Summary</span>
              </div>
              <Badge variant="outline" className="text-xs">
                Generated: {formatDate(scheduleHData.generated_timestamp)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Summary Totals */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">Total Direct Costs</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(calculateTotals(scheduleHData).directCosts)}
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">Total Indirect Costs</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(calculateTotals(scheduleHData).indirectCosts)}
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">Grand Total</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(calculateTotals(scheduleHData).totalCosts)}
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">Indirect Rate</div>
                <div className="text-lg font-semibold">
                  {calculateTotals(scheduleHData).directCosts > 0 
                    ? `${((calculateTotals(scheduleHData).indirectCosts / calculateTotals(scheduleHData).directCosts) * 100).toFixed(1)}%`
                    : '0.0%'
                  }
                </div>
              </div>
            </div>

            {/* Detailed Contract Table */}
            <div className="mt-6">
              <h4 className="font-semibold mb-4 text-slate-700">Contract-by-Contract Summary</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contract Information</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Direct Costs</TableHead>
                      <TableHead className="text-right">Fringe</TableHead>
                      <TableHead className="text-right">Overhead</TableHead>
                      <TableHead className="text-right">G&A</TableHead>
                      <TableHead className="text-right">IR&D/B&P</TableHead>
                      <TableHead className="text-right">COM</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleHData.contract_summaries?.map((contract, index) => {
                      const totalCosts = (contract.total_direct_costs || 0) + (contract.total_indirect_costs || 0);
                      const percentageOfTotal = calculateTotals(scheduleHData).totalCosts > 0 
                        ? (totalCosts / calculateTotals(scheduleHData).totalCosts) * 100 
                        : 0;

                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            <div>
                              <div className="font-medium">{contract.contract_id}</div>
                              <div className="text-xs text-slate-500">{contract.contract_name}</div>
                              {contract.pop_start && contract.pop_end && (
                                <div className="flex items-center text-xs text-slate-500 mt-1">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {new Date(contract.pop_start).toLocaleDateString()} - {new Date(contract.pop_end).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{contract.customer_name || 'N/A'}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {getContractTypeLabel(contract.contract_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(contract.total_direct_costs || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(contract.indirect_costs?.fringe || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(contract.indirect_costs?.overhead || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(contract.indirect_costs?.ga || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency((contract.indirect_costs?.ird || 0) + (contract.indirect_costs?.bp || 0))}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(contract.indirect_costs?.com || 0)}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-indigo-600">
                            {formatCurrency(totalCosts)}
                          </TableCell>
                          <TableCell className="text-right">
                            {percentageOfTotal.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Totals Row */}
                    <TableRow className="border-t-2 border-slate-300 font-semibold bg-slate-50">
                      <TableCell colSpan={3} className="font-bold">TOTAL</TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(calculateTotals(scheduleHData).directCosts)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(scheduleHData.indirect_totals?.fringe || 0)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(scheduleHData.indirect_totals?.overhead || 0)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(scheduleHData.indirect_totals?.ga || 0)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency((scheduleHData.indirect_totals?.ird || 0) + (scheduleHData.indirect_totals?.bp || 0))}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(scheduleHData.indirect_totals?.com || 0)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-indigo-600">
                        {formatCurrency(calculateTotals(scheduleHData).totalCosts)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        100.0%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Rate Analysis */}
            {scheduleHData.rate_analysis && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-3 text-blue-900">Indirect Rate Analysis</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-sm">
                    <div className="text-blue-700">Fringe Rate</div>
                    <div className="font-medium">{(scheduleHData.rate_analysis.fringe_rate * 100).toFixed(2)}%</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-blue-700">Overhead Rate</div>
                    <div className="font-medium">{(scheduleHData.rate_analysis.overhead_rate * 100).toFixed(2)}%</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-blue-700">G&A Rate</div>
                    <div className="font-medium">{(scheduleHData.rate_analysis.ga_rate * 100).toFixed(2)}%</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-blue-700">Combined Rate</div>
                    <div className="font-medium">{(scheduleHData.rate_analysis.combined_rate * 100).toFixed(2)}%</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-blue-700">Effective Rate</div>
                    <div className="font-medium">
                      {calculateTotals(scheduleHData).directCosts > 0 
                        ? `${((calculateTotals(scheduleHData).indirectCosts / calculateTotals(scheduleHData).directCosts) * 100).toFixed(2)}%`
                        : '0.00%'
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="text-center py-8">
            <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium mb-2 text-slate-600">Schedule H Not Generated</h3>
            <p className="text-sm text-slate-500 mb-4">
              Generate Schedule H to view contract cost summaries with direct and indirect cost allocations.
            </p>
            {contracts.length === 0 ? (
              <div className="text-sm text-amber-600 mb-4">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Please add contracts first before generating Schedule H.
              </div>
            ) : (
              <Button
                onClick={handleGenerateScheduleH}
                disabled={generating}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Schedule H
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Information Panel */}
      <div className="mt-6 p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-medium">Schedule H Information</p>
            <ul className="text-xs mt-1 space-y-1">
              <li>• Contract Summary: High-level view of all contracts with direct and indirect costs</li>
              <li>• DCAA Compliance: Formatted according to DCAA requirements for ICE submission</li>
              <li>• Cost Allocation: Shows how indirect rates are applied to each contract</li>
              <li>• Percentage Analysis: Contract cost distribution across the company portfolio</li>
              <li>• Export Ready: Automatically formatted for Schedule H inclusion in ICE Model</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleHSummary;
